// apps/client/src/pages/admin/dashboard/admin/components/GetUserDialog.tsx

import { useDialog } from "@/client/stores/dialog";
import { describePromoCredits } from "@/client/services/credit/expiry";
import { useGetUser } from "@/client/services/user/get-specific-user";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@reactive-resume/ui";
import {
  CheckCircleIcon,
  UserIcon,
  EnvelopeSimpleIcon,
  CalendarIcon,
  LinkIcon,
  UsersThreeIcon,
  ShieldCheckIcon,
  CoinsIcon,
  XCircleIcon,
  XIcon,
} from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

// Composant réutilisable pour les cartes d'information
const InfoCard = ({
  icon,
  label,
  value,
  iconBg = "bg-admin-surface-elevated",
  iconColor = "text-admin-text-secondary",
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  iconBg?: string;
  iconColor?: string;
}) => (
  <div className="flex items-center gap-3 rounded-lg border border-admin-border bg-admin-background p-3">
    <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-full", iconBg)}>
      <span className={iconColor}>{icon}</span>
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-xs text-admin-text-muted">{label}</p>
      <div className="truncate text-sm font-medium text-admin-text-primary">{value}</div>
    </div>
  </div>
);

export const GetUserDialog = () => {
  const { isOpen, close, payload } = useDialog<{ id: string }>("get-user");
  const userId = payload?.item?.id;
  console.log(userId);
  const { user, isLoading } = useGetUser(userId);

  if (!user) return null;

  const roles = user.roles?.map((ur: any) => ur.name) || [];
  console.log(roles);

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent className="w-[95vw] max-w-2xl overflow-hidden p-0 sm:w-full">
        {/* Header personnalisé */}
        <DialogHeader className="border-b border-admin-border px-4 pb-3 pt-4 sm:px-6 sm:pb-4 sm:pt-6">
          <div className="flex items-start justify-between gap-2">
            <div className="flex min-w-0 items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-admin-primary/10 sm:h-12 sm:w-12">
                <UserIcon size={20} weight="duotone" className="text-admin-primary sm:hidden" />
                <UserIcon
                  size={24}
                  weight="duotone"
                  className="hidden text-admin-primary sm:block"
                />
              </div>
              <div className="min-w-0">
                <DialogTitle className="truncate text-base font-bold text-admin-text-primary sm:text-xl">
                  Détails de l'utilisateur
                </DialogTitle>
                <p className="truncate text-xs text-admin-text-muted sm:text-sm">
                  Informations et statut du compte
                </p>
              </div>
            </div>
            {/* <Button
              variant="ghost"
              size="icon"
              onClick={close}
              className="shrink-0 text-admin-text-muted hover:text-admin-text-primary"
              aria-label="Fermer"
            >
              <XIcon size={20} />
            </Button> */}
          </div>
        </DialogHeader>

        {/* Corps du dialogue */}
        <div className="max-h-[70vh] space-y-3 overflow-y-auto p-4 sm:space-y-4 sm:p-6">
          {isLoading ? (
            <div className="py-8 text-center text-admin-text-muted">Chargement...</div>
          ) : (
            <>
              {/* Bannière du profil */}
              <div className="flex flex-col gap-3 rounded-xl bg-admin-surface-elevated p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4">
                <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-admin-surface sm:h-14 sm:w-14">
                    {user.picture ? (
                      <img
                        src={user.picture}
                        alt={user.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <UserIcon size={28} weight="duotone" className="text-admin-text-muted" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-base font-semibold text-admin-text-primary sm:text-lg">
                      {user.name || "—"}
                    </p>
                    <p className="truncate text-xs text-admin-text-muted sm:text-sm">
                      {user.email}
                    </p>
                  </div>
                </div>
                <Badge
                  variant={user.isActivated ? "success" : "error"}
                  className="shrink-0 self-start px-3 py-1 text-xs font-medium sm:self-auto"
                  outline={false}
                >
                  {user.isActivated ? "Actif" : "Banni"}
                </Badge>
              </div>

              {/* Grille : 1 colonne mobile → 2 colonnes sm+ */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {/* <InfoCard
                  icon={<UserIcon size={18} weight="duotone" />}
                  label="Noms"
                  value={user.name || "—"}
                />
                <InfoCard
                  icon={<EnvelopeSimpleIcon size={18} weight="duotone" />}
                  label="Email"
                  value={user.email}
                /> */}
                <InfoCard
                  icon={<CalendarIcon size={18} weight="duotone" />}
                  label="Inscription"
                  value={format(new Date(user.createdAt), "dd/MM/yyyy", { locale: fr })}
                />
                <InfoCard
                  icon={<CalendarIcon size={18} weight="duotone" />}
                  label="Dernière modification"
                  value={format(new Date(user.updatedAt), "dd/MM/yyyy", { locale: fr })}
                />
                <InfoCard
                  icon={<LinkIcon size={18} weight="duotone" />}
                  label="Dernière connexion"
                  value={
                    user.secrets?.lastSignedIn
                      ? format(new Date(user.secrets.lastSignedIn), "dd/MM/yyyy", {
                          locale: fr,
                        })
                      : "—"
                  }
                />
                <InfoCard
                  icon={<CoinsIcon size={18} weight="duotone" />}
                  label="Crédits de Telechargement"
                  value={user.credit ?? 0}
                />
                {/* <InfoCard
                icon={<ShieldCheckIcon size={18} weight="duotone" />}
                label="Activé"
                value={
                  user.isActivated ? (
                    <span className="flex items-center gap-2 text-admin-success">
                      <CheckCircleIcon size={18} weight="fill" />
                      Oui
                    </span>
                  ) : (
                    <span className="flex items-center gap-2 text-admin-danger">
                      <XCircleIcon size={18} weight="fill" />
                      Non
                    </span>
                  )
                }
              /> */}
              </div>

              {/* Cartes pleine largeur */}
              {(user.creditExpiring.length > 0 || user.creditExpired > 0) && (
                <InfoCard
                  icon={<CalendarIcon size={18} weight="duotone" />}
                  label="Crédits promo"
                  value={describePromoCredits(user.creditExpiring, user.creditExpired)}
                />
              )}
              <InfoCard
                icon={<UsersThreeIcon size={18} weight="duotone" />}
                label="Rôles"
                value={
                  roles.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {roles.map((role: string) => (
                        <Badge key={role} className="border-focus bg-focus text-xs text-white">
                          {role}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <Badge
                      key={"noneRole"}
                      className="bg-focus-orange text-xs text-admin-text-primary"
                    >
                      N/A
                    </Badge>
                  )
                }
              />

              {/* <InfoCard
                icon={<EnvelopeSimpleIcon size={18} weight="duotone" />}
                label="Email vérifié"
                value={
                  user.emailVerified ? (
                    <span className="flex items-center gap-2 text-admin-success">
                      <CheckCircleIcon size={18} weight="fill" />
                      Oui
                    </span>
                  ) : (
                    <span className="flex items-center gap-2 text-admin-text-muted">
                      <XCircleIcon size={18} weight="fill" />
                      Non
                    </span>
                  )
                }
              /> */}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse gap-2 border-t border-admin-border bg-admin-surface px-4 py-3 sm:flex-row sm:justify-end sm:px-6 sm:py-4">
          <Button
            onClick={close}
            className="w-full bg-admin-primary text-admin-primary-foreground hover:bg-admin-primary/90 sm:w-auto"
          >
            Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
